import getSDK, {SDK} from '@ua/web-sdk'
import {View} from '@ua/web-sdk/lib/iax/types'

import createDefaultUserMetaClient, {UserMetaClient} from './user-meta'

enum Groups {
  Device = 'device',
}

export type Group = `${Groups}`

type ValidTagMapping = {[key in Group]: string[]}

interface ValidTags extends ValidTagMapping {
  ['device']: []
}

export interface WebSdkWrapper {
  addDeviceTags<T extends Group>(
    group: T,
    ...tags: ValidTags[T][number][]
  ): Promise<void>
  addContactTags<T extends Group>(
    group: T,
    ...tags: ValidTags[T][number][]
  ): Promise<void>
  /**
   * Track an event as a custom event. This method is public because it will be
   * called from the `track` module, but should not be called directly and so
   * has a `_` prefixed name.
   * @private
   * @param action the action that occurred
   * @param context the context in which it occurred
   * @param description description of the action
   * @param properties any additional properties that go along with the action
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _track(name: string, properties: Record<string, unknown>): Promise<void>
  trackScreen(name: string): Promise<void>
  createEmbeddedView(element: HTMLElement, embeddedId: string): View
}

class WebSdkWrapperImpl implements WebSdkWrapper {
  /**
   * A note on potential version mismatches:
   * - The Typescript SDK interface comes from @ua/web-sdk, whose version is specified in package.json.
   * - The actual SDK is downloaded dynamically from TINCAN_WEB_SDK_URL, so new versions are
   *   automatically used as soon as they're published to that URL. In addition, the URL can
   *   differ between environments, such as using `v2` in prod and `next` in staging.
   * This means that the Typescript types can potentially differ from the true SDK interface,
   * although we try to keep that to a minimum by avoiding interface changes when possible.
   */
  private _sdk: SDK
  private _client: UserMetaClient

  constructor(sdk: SDK, client: UserMetaClient) {
    this._sdk = sdk
    this._client = client
  }

  async init(): Promise<void> {
    const meta = await this._client.get()
    // if we don't have a channel id yet, create a channel for the browser
    if (!(await this._sdk.channel.id())) {
      await this._sdk.create()
      await this._sdk.channel.editTags().add('device', meta.environment).apply()
    }
    // set the named user id if it doesn't match what we have stored, but don't
    // unset it if not provided by the backend
    if (meta.named_user_id) {
      await this._sdk.contact.identify(meta.named_user_id)
    }
  }

  async addDeviceTags<T extends Group>(
    group: T,
    ...tags: ValidTags[T][number][]
  ): Promise<void> {
    await this._sdk.channel.editTags().add(group, tags).apply()
  }

  async addContactTags<T extends Group>(
    group: T,
    ...tags: ValidTags[T][number][]
  ): Promise<void> {
    await this._sdk.contact.editTags().add(group, tags).apply()
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  async _track(
    name: string,
    properties: Record<string, unknown>
  ): Promise<void> {
    const event = new this._sdk.CustomEvent(name, undefined, properties)

    await event.track()
  }

  async trackScreen(name: string): Promise<void> {
    return this._sdk.analytics.trackScreen(name)
  }

  createEmbeddedView(element: HTMLElement, embeddedId: string): View {
    return this._sdk.components.embeddedViews.create(element, embeddedId)
  }
}

// the singleton wrapper is stored here
let defaultSdk: Promise<WebSdkWrapper> | null

async function initDefaultSdk(): Promise<WebSdkWrapperImpl> {
  const sdk = await getSDK()
  const client = createDefaultUserMetaClient()

  const wrapper = new WebSdkWrapperImpl(sdk, client)
  await wrapper.init()

  return wrapper
}

/**
 * Returns a singleton instance of the Web SDK wrapper, which provides an
 * interface for the Airship Web SDK. If the singleton instance isn't
 * initialized yet, it is created.
 *
 * When initialized, the SDK will register a channel for the current browser,
 * and associate it to a stable Named User ID for the user.
 * @returns a singleton web sdk wrapper
 */
export default async function getDefaultSdk(): Promise<WebSdkWrapper> {
  if (defaultSdk) {
    return defaultSdk
  }

  defaultSdk = initDefaultSdk()

  return defaultSdk
}

/**
 * EXPORTED FOR TESTING ONLY. Creates a web sdk wrapper with the provided
 * dependencies.
 *
 * @private
 * @param sdk the web sdk global
 * @param client user metadata client
 * @param storage web sdk storage
 * @returns a web sdk wrapper
 */
export async function createSdk(
  sdk: SDK,
  client: UserMetaClient
): Promise<WebSdkWrapper> {
  const wrapper = new WebSdkWrapperImpl(sdk, client)
  await wrapper.init()

  return wrapper
}
